import { api_ } from '@shared/plugIn/axios';
import { LowerRole } from '@shared/types';

import {
  GiftPartner,
  GiftPartnerApplyDto,
  GiftPartnerChange,
  GiftPartnerContact,
  GiftPartnerOverview,
  GiftPartnerPingResult,
  GiftPartnerUpdateDto,
  GiftPartnerWebhookEvent,
  GiftPartnerWebhookEventType,
} from './giftPartner.interface';

export type GiftPartnerRole = LowerRole.Admin | LowerRole.GiftBrandSeller;
const base = (role: GiftPartnerRole) => `/${role}/gift-partner`;
export const isGiftPartnerNotDeployed = (e: unknown) =>
  (e as { response?: { status?: number } })?.response?.status === 404;
export const getGiftPartners = async (
  role: GiftPartnerRole,
  params?: { status?: string; pending?: '1' },
) => (await api_.get<GiftPartner[]>(base(role), { params })).data;
export const getGiftPartnerOverview = async () =>
  (await api_.get<GiftPartnerOverview>(`${base(LowerRole.GiftBrandSeller)}/overview`))
    .data;
export const applyGiftPartner = async (dto: GiftPartnerApplyDto) =>
  (await api_.post<GiftPartner>(base(LowerRole.GiftBrandSeller), dto)).data;
export const createGiftPartner = async (dto: GiftPartnerUpdateDto) =>
  (await api_.post<{ partner: GiftPartner; apiKey: string }>(base(LowerRole.Admin), dto))
    .data;
export const requestGiftPartnerChange = async (id: string, dto: GiftPartnerChange) =>
  (
    await api_.post<GiftPartner>(
      `${base(LowerRole.GiftBrandSeller)}/${id}/change-request`,
      dto,
    )
  ).data;
export const updateGiftPartnerContact = async (id: string, contact: GiftPartnerContact) =>
  (
    await api_.patch<GiftPartner>(
      `${base(LowerRole.GiftBrandSeller)}/${id}/contact`,
      contact,
    )
  ).data;
export const issueGiftPartnerKey = async (
  role: GiftPartnerRole,
  id: string,
  label?: string,
) =>
  (
    await api_.post<{ partner: GiftPartner; apiKey: string }>(
      `${base(role)}/${id}/api-key`,
      { label },
    )
  ).data;
export const revokeGiftPartnerKey = async (
  role: GiftPartnerRole,
  id: string,
  keyId: string,
) => (await api_.delete<GiftPartner>(`${base(role)}/${id}/api-key/${keyId}`)).data;
export const setGiftPartnerWebhook = async (
  role: GiftPartnerRole,
  id: string,
  dto: { isEnabled?: boolean; events?: GiftPartnerWebhookEventType[]; url?: string },
) =>
  (
    await api_.put<{ partner: GiftPartner; webhookSecret?: string }>(
      `${base(role)}/${id}/webhook`,
      dto,
    )
  ).data;
export const rotateGiftPartnerWebhookSecret = async (role: GiftPartnerRole, id: string) =>
  (
    await api_.post<{ webhookSecret: string }>(
      `${base(role)}/${id}/webhook/rotate-secret`,
    )
  ).data;
export const pingGiftPartnerWebhook = async (role: GiftPartnerRole, id: string) =>
  (await api_.post<GiftPartnerPingResult>(`${base(role)}/${id}/webhook/ping`)).data;
export const getGiftPartnerWebhookEvents = async (
  role: GiftPartnerRole,
  id: string,
  params?: { status?: string; limit?: number },
) =>
  (
    await api_.get<GiftPartnerWebhookEvent[]>(`${base(role)}/${id}/webhook-event`, {
      params,
    })
  ).data;
export const resendGiftPartnerWebhookEvent = async (
  role: GiftPartnerRole,
  id: string,
  eventId: string,
) =>
  (
    await api_.post<GiftPartnerWebhookEvent>(
      `${base(role)}/${id}/webhook-event/${eventId}/resend`,
    )
  ).data;
export const approveGiftPartner = async (id: string) =>
  (await api_.post<GiftPartner>(`${base(LowerRole.Admin)}/${id}/approve`)).data;
export const rejectGiftPartner = async (id: string, reason: string) =>
  (await api_.post<GiftPartner>(`${base(LowerRole.Admin)}/${id}/reject`, { reason }))
    .data;
export const updateGiftPartner = async (id: string, dto: GiftPartnerUpdateDto) =>
  (await api_.patch<GiftPartner>(`${base(LowerRole.Admin)}/${id}`, dto)).data;

// Only display known messages; never log/store an AxiosError containing response bodies.
export const giftPartnerError = (error: unknown): string => {
  const response = (
    error as { response?: { status?: number; data?: { message?: string | string[] } } }
  )?.response;
  const message = String(response?.data?.message ?? '');
  if (response?.status === 409)
    return '이미 신청(또는 연동)된 브랜드가 포함되어 있습니다. 최신 현황을 확인해 주세요. 승인 중 충돌한 요청은 반려 후 다시 신청해 주세요.';
  if (message.startsWith('invalid ip:'))
    return `올바른 IP가 아닙니다: ${message.slice(11).trim()}`;
  if (message.includes('allowedIps required')) return 'IP를 1개 이상 입력하세요';
  if (message.includes('https only')) return '콜백 URL은 https 주소만 가능합니다';
  if (message.includes('internal address') || message.includes('internal host'))
    return '콜백 URL에 내부망 주소는 등록할 수 없습니다';
  if (message.includes('malformed url')) return '콜백 URL 주소 형식을 확인해 주세요';
  if (message.includes('credentials in url'))
    return '콜백 URL에 계정정보를 포함할 수 없습니다';
  if (message.includes('within your brands')) return '담당 브랜드만 선택할 수 있습니다';
  if (message.includes('active api keys limited'))
    return '사용 중인 키는 2개까지입니다. 하나를 폐기한 뒤 발급하세요.';
  if (message.includes('nothing to approve') || message.includes('nothing to reject'))
    return '이미 처리된 요청입니다. 최신 현황을 확인해 주세요.';
  if (message.includes('active api key not found'))
    return '이미 폐기된 키입니다. 최신 현황을 확인해 주세요.';
  if (message.includes('resendable event not found'))
    return '이미 전달되었거나 재발송할 수 없는 이력입니다.';
  if (
    message.includes('available after approval') ||
    message.includes('approve the application first')
  )
    return '승인 후 이용할 수 있습니다';
  if (message.includes('webhook url required')) return '콜백 URL을 먼저 등록해 주세요';
  if (response?.status === 401 || response?.status === 403)
    return '현재 계정으로 이 연동을 관리할 수 없습니다.';
  return '처리하지 못했습니다. 최신 상태를 확인한 후 다시 시도해 주세요.';
};
